import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Input,
  Typography,
  TextField,
  Snackbar,
} from "@material-ui/core";
import { Autocomplete, Alert } from "@material-ui/lab";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus,
  faUserCheck,
  faPhone,
  faEnvelope,
  faHome,
} from "@fortawesome/free-solid-svg-icons";

import { _t } from "../../../i18n/lang";
import { customerTypeDictionary } from "../../../constants/customerType";
import { modernReportingService } from "../../../services/modernReportingService";
import AjaxLoading from "../../AjaxLoading";

const useStyles = makeStyles((theme) => ({
  wrap: {
    marginTop: "20px",
    marginBottom: "50px",
  },
  paper: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  textRight: {
    textAlign: "right",
  },
  buttonIcon: {
    marginRight: "4px",
  },
}));

export default function AssignedCustomers(props) {
  const [assignedCustomers, setAssignedCustomers] = useState([]);
  const [assignedCustomer, setAssignedCustomer] = useState(null);
  const [assignedQty, setAssignedQty] = useState(1);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    type: "success",
    duration: 6000,
  });
  const [listLoading, setListLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const classes = useStyles();

  useEffect(() => {
    async function fetchData() {
      setListLoading(true);
      // eslint-disable-next-line react/prop-types
      const { detail } = props;
      const { customers } =
        await modernReportingService.customersAssignedToDetails(detail);
      setAssignedCustomers(customers);
      setListLoading(false);
    }

    fetchData();
  }, []);

  // eslint-disable-next-line react/prop-types
  const { detail, customers } = props;

  const availableCustomers = () => {
    if (!customers) {
      return [];
    }
    return customers;
  };
  const qtyAvailable = (key) => {
    if (!detail) {
      return 0;
    }
    let usedQty = assignedCustomers.reduce(
      (value, fields) => value + (fields[key] || 0),
      0,
    );
    return detail.qtyOrdered - usedQty;
  };

  const addingIsAvailable = () => qtyAvailable("connectionQty") > 0;

  const handleAssignedCustomer = (event, newValue) => {
    setAssignedCustomer(newValue);
  };

  const handleAssignedQty = (event) => {
    let value = parseInt(event.target.value, 10);
    let availableValue = qtyAvailable("connectionQty");
    if (value < 1) {
      value = 1;
    }
    if (value > availableValue) {
      value = availableValue;
    }
    setAssignedQty(value);
  };

  const assignCustomerToLine = async () => {
    if (!assignedQty) {
      showAlert("error", _t("MMR_INVALID_QTY_VALUE"));
      return false;
    }

    if (!assignedCustomer) {
      showAlert("error", _t("MMR_INVALID_CUSTOMER_VALUE"));
      return false;
    }

    const assigned = { ...assignedCustomer, connectionQty: assignedQty };

    setFormLoading(true);
    // eslint-disable-next-line react/prop-types
    const { detail } = props;
    const { messageBody, messageType, connectionId } =
      await modernReportingService.assignCustomerToDetail(detail, assigned);
    setFormLoading(false);
    showAlert(messageType, messageBody);

    if (!connectionId) {
      return false;
    }

    assignedCustomers.push(assigned);
    setAssignedCustomers(assignedCustomers);
    setAssignedQty(0);
    setAssignedCustomer(null);
  };

  const showAlert = (type, message, duration = 6000) => {
    setAlert({
      open: true,
      message,
      type,
      duration,
    });
  };

  const closeAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlert({
      open: false,
      message: "",
      type: "success",
    });
  };

  const alertBorder = (type) => {
    let alertBorderColor = "#28a745";

    if (type === "info") {
      alertBorderColor = "#2196f3";
    }

    if (alert.type === "warning") {
      alertBorderColor = "#ff9800";
    }

    if (alert.type === "error") {
      alertBorderColor = "#8a1507";
    }

    return alertBorderColor;
  };

  const alertBorderColor = alertBorder(alert.type);

  return (
    <>
      {/* eslint-disable-next-line react/prop-types */}
      <Box sx={{ flexGrow: 1 }}>
        <Snackbar
          open={alert.open}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={6000}
          onClose={closeAlert}
        >
          {alert.open ? (
            <Alert
              onClose={closeAlert}
              severity={alert.type}
              style={{ border: `1px solid ${alertBorderColor}` }}
            >
              <div dangerouslySetInnerHTML={{ __html: alert.message }} />
            </Alert>
          ) : (
            <span></span>
          )}
        </Snackbar>
        <Grid container spacing={2}>
          <Grid item xs={7}>
            <TableContainer sx={{ maxHeight: 220 }} className={classes.wrap}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">
                      {_t("MMR_ASSIGNED_CUSTOMER_LIST_HEADER_INDEX")}
                    </TableCell>
                    <TableCell align="center">
                      {_t("MMR_ASSIGNED_CUSTOMER_LIST_HEADER_ID")}
                    </TableCell>
                    <TableCell align="center">
                      {_t("MMR_ASSIGNED_CUSTOMER_LIST_HEADER_CUSTOMER_NAME")}
                    </TableCell>
                    <TableCell align="center">
                      {_t("MMR_ASSIGNED_CUSTOMER_LIST_HEADER_CUSTOMER_DATA")}
                    </TableCell>
                    <TableCell align="center">
                      {_t("MMR_ASSIGNED_CUSTOMER_LIST_HEADER_CUSTOMER_QTY")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listLoading ? (
                    <TableRow>
                      <TableCell align="center" colSpan={5}>
                        <div
                          style={{ position: "relative", minHeight: "10em" }}
                        >
                          <AjaxLoading />
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {assignedCustomers.map((customer, index) => (
                        <TableRow key={`${index}_${customer.customerId}`}>
                          <TableCell align="center">{index + 1}</TableCell>
                          <TableCell align="center">
                            {customer.customerId}
                          </TableCell>
                          <TableCell align="left">
                            {customer.customerType ===
                              customerTypeDictionary.endCustomer && (
                              <>
                                <small>
                                  {customer.customerFirstName}{" "}
                                  {customer.customerLastName}
                                </small>
                              </>
                            )}
                            {customer.customerType ===
                              customerTypeDictionary.company && (
                              <>
                                <small>{customer.customerFirm}</small>
                                <small style={{ marginLeft: "4px" }}>
                                  ({customer.customerTaxId})
                                </small>
                              </>
                            )}
                          </TableCell>
                          <TableCell align="left">
                            {customer.customerEmail && (
                              <div>
                                <small>
                                  <FontAwesomeIcon
                                    icon={faEnvelope}
                                    className={classes.buttonIcon}
                                  />
                                  {customer.customerEmail}
                                </small>
                              </div>
                            )}
                            {customer.customerPhone && (
                              <div>
                                <small>
                                  <FontAwesomeIcon
                                    icon={faPhone}
                                    className={classes.buttonIcon}
                                  />
                                  {customer.customerPhone}
                                </small>
                              </div>
                            )}
                            {customer.customerAddress && (
                              <div>
                                <small>
                                  <FontAwesomeIcon
                                    icon={faHome}
                                    className={classes.buttonIcon}
                                  />
                                  {customer.customerAddress},{" "}
                                  {customer.customerPostal}{" "}
                                  {customer.customerCity}
                                </small>
                              </div>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {customer.connectionQty}
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={5}>
            <Paper className={`${classes.paper} ${classes.wrap}`}>
              <Box
                component="form"
                sx={{
                  "& .MuiTextField-root": { m: 1, width: "25ch" },
                }}
                noValidate
                autoComplete="off"
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {addingIsAvailable() && (
                      <Alert
                        onClose={closeAlert}
                        severity={"info"}
                        style={{
                          border: `1px solid #2196f3`,
                          marginTop: "10px",
                          marginBottom: "10px",
                        }}
                      >
                        <div>{_t("MMR_INGO_TO_ASSIGN_LEFT")}</div>
                      </Alert>
                    )}

                    <Typography variant="h6" gutterBottom>
                      <FontAwesomeIcon
                        icon={faUserPlus}
                        className={classes.buttonIcon}
                      />
                      {_t("MMR_ASSIGN_CUSTOMER_TO_LINE")}
                    </Typography>
                  </Grid>
                </Grid>
                {formLoading ? (
                  <div style={{ position: "relative", minHeight: "10em" }}>
                    <AjaxLoading />
                  </div>
                ) : (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <FormControl fullWidth variant="standard">
                          <Autocomplete
                            id="free-solo-demo"
                            freeSolo
                            onChange={handleAssignedCustomer}
                            options={availableCustomers()}
                            getOptionLabel={(option) => {
                              let name = `${option.customerFirm} (${option.customerTaxId})`;

                              if (
                                option.customerType ===
                                customerTypeDictionary.endCustomer
                              ) {
                                name = `${option.customerFirstName} ${option.customerLastName}`;
                              }
                              return name;
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={_t(
                                  "MMR_ASSIGN_CUSTOMER_SEARCH_CUSTOMER",
                                )}
                              ></TextField>
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl fullWidth variant="standard">
                          <InputLabel
                            htmlFor="standard-adornment-qty"
                            style={{ lineHeight: "0 !important" }}
                          >
                            {_t("MMR_ASSIGN_CUSTOMER_QTY_AMOUNT")}
                          </InputLabel>
                          <Input
                            id="standard-adornment-qty"
                            type="number"
                            onChange={handleAssignedQty}
                            value={assignedQty}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </>
                )}
                <Grid container spacing={2} justify={"flex-end"}>
                  <Grid item xs={6} className={classes.textRight}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={assignCustomerToLine}
                      disabled={!addingIsAvailable()}
                    >
                      <FontAwesomeIcon
                        icon={faUserCheck}
                        className={classes.buttonIcon}
                      />
                      {_t("MMR_ADD_CUSTOMER")}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

